import { useState, useMemo, useEffect } from "react";
import { CalendarBlank, MapPin, CaretRight, Storefront, CurrencyDollar, NavigationArrow, ArrowDown, BookmarkSimple } from "@phosphor-icons/react";
import { ScrollArea } from "./ui/scroll-area";
import { format } from "date-fns";

const EventCard = ({ event, onClick, isSelected, isSaved, onToggleSave }) => {
  const getCategoryStyle = (category) => {
    const styles = {
      festival: "badge-festival",
      fair: "badge-fair",
      community: "badge-community"
    };
    return styles[category] || "badge-community";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      festival: "Festival",
      fair: "Fair",
      community: "Community"
    };
    return labels[category] || category;
  };

  const formatEventDate = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (startDate === endDate) {
      return format(start, "MMM d, yyyy");
    }
    
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${format(start, "MMM d")} - ${format(end, "d, yyyy")}`;
    }
    
    return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
  };

  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  return (
    <div
      className={`event-card bg-white rounded-xl border p-2.5 sm:p-3 cursor-pointer ${
        isSelected 
          ? "border-[#E07A5F] ring-2 ring-[#E07A5F]/20" 
          : "border-[#D6D3D1] hover:border-[#81B29A]"
      }`}
      onClick={() => onClick(event)}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex gap-3 sm:gap-4">
        {/* Event Image */}
        <div className="relative w-24 h-24 sm:w-36 sm:h-36 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={event.image_url}
            alt={event.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400";
            }}
          />
          <div className="absolute inset-0 image-overlay" />
          <span className={`absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider ${getCategoryStyle(event.category)} bg-white/90 backdrop-blur-sm`}>
            {getCategoryLabel(event.category)}
          </span>
        </div>

        {/* Event Details */}
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5 sm:py-1">
          <div>
            <h3 
              className="font-semibold text-[#2B2D42] text-sm sm:text-base line-clamp-2"
              style={{ fontFamily: "'Work Sans', sans-serif" }}
            >
              {event.name}
            </h3>
            
            <div className="flex items-center gap-1.5 mt-1.5 sm:mt-2 text-xs sm:text-sm text-[#6C757D]">
              <MapPin weight="duotone" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#81B29A] flex-shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
            
            <div className="flex items-center gap-1.5 mt-1 text-xs sm:text-sm text-[#6C757D]">
              <CalendarBlank weight="duotone" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#E07A5F] flex-shrink-0" />
              <span>{formatEventDate(event.start_date, event.end_date)}</span>
            </div>
          </div>
          
          {/* Vendor Info */}
          <div className="mt-1.5 sm:mt-2 pt-1.5 sm:pt-2 border-t border-[#D6D3D1]/50 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {event.distance_miles != null && (
                <div className="flex items-center gap-1 text-xs sm:text-sm">
                  <NavigationArrow weight="duotone" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#E07A5F]" />
                  <span className="text-[#E07A5F] font-semibold">{event.distance_miles} mi</span>
                </div>
              )}
              <div className="flex items-center gap-1 text-xs sm:text-sm">
                <CurrencyDollar weight="duotone" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#81B29A]" />
                {event.booth_fee ? (
                  <span className="text-[#2B2D42] font-medium">{event.booth_fee}</span>
                ) : (
                  <span className="text-[#E07A5F] font-medium text-xs">Contact for Info</span>
                )}
              </div>
              
              {event.expected_attendance && (
                <div className="flex items-center gap-1 text-xs sm:text-sm text-[#6C757D] hidden sm:flex">
                  <span>{event.expected_attendance}</span>
                </div>
              )}
            </div>
            
            {event.vendor_app_url && !isDeadlinePassed(event.app_deadline) && (
              <span className="text-[10px] sm:text-xs text-[#81B29A] font-semibold uppercase">Open</span>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-between self-stretch py-1 flex-shrink-0">
          {onToggleSave && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSave(event.id); }}
              className={`p-1 rounded-md transition-colors ${isSaved ? "text-[#E07A5F]" : "text-[#A8A5A1] hover:text-[#E07A5F]"}`}
              data-testid={`bookmark-${event.id}`}
              title={isSaved ? "Remove from saved" : "Save this show"}
            >
              <BookmarkSimple weight={isSaved ? "fill" : "regular"} className="w-5 h-5" />
            </button>
          )}
          <CaretRight weight="bold" className="w-4 h-4 text-[#6C757D] hidden sm:block" />
        </div>
      </div>
    </div>
  );
};

const EventListSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-[#D6D3D1] p-2.5 sm:p-3">
        <div className="flex gap-3 sm:gap-4">
          <div className="w-24 h-24 sm:w-36 sm:h-36 rounded-lg skeleton flex-shrink-0" />
          <div className="flex-1 space-y-2 sm:space-y-3 py-1">
            <div className="w-3/4 h-4 sm:h-5 rounded skeleton" />
            <div className="w-1/2 h-3 sm:h-4 rounded skeleton" />
            <div className="w-2/3 h-3 sm:h-4 rounded skeleton" />
            <div className="w-1/2 h-3 sm:h-4 rounded skeleton" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="empty-state rounded-xl border border-[#D6D3D1] p-8 text-center col-span-full">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E07A5F]/10 flex items-center justify-center">
      <Storefront weight="duotone" className="w-8 h-8 text-[#E07A5F]" />
    </div>
    <h3 
      className="text-lg font-semibold text-[#2B2D42] mb-2"
      style={{ fontFamily: "'Work Sans', sans-serif" }}
    >
      No Shows Found
    </h3>
    <p className="text-sm text-[#6C757D] max-w-sm mx-auto">
      Try adjusting your filters or search terms to discover more vendor opportunities across the US.
    </p>
  </div>
);

const EVENTS_PER_PAGE = 40;

const EventList = ({ events, loading, onEventClick, selectedEvent, nearMeActive, savedShowIds, onToggleSave }) => {
  const [visibleCount, setVisibleCount] = useState(EVENTS_PER_PAGE);

  // Reset pagination when events change (new search/filter)
  useEffect(() => {
    setVisibleCount(EVENTS_PER_PAGE);
  }, [events]);

  const visibleEvents = events.slice(0, visibleCount);
  const hasMore = visibleCount < events.length;

  return (
    <div 
      className="bg-white rounded-xl border border-[#D6D3D1] shadow-sm overflow-hidden"
      data-testid="event-list-container"
    >
      {/* Header */}
      <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-[#D6D3D1]/50 bg-[#F7F5F0]/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Storefront weight="duotone" className="w-5 h-5 text-[#E07A5F]" />
            <h2 
              className="text-base sm:text-lg font-semibold text-[#2B2D42]"
              style={{ fontFamily: "'Work Sans', sans-serif" }}
            >
              {nearMeActive ? "Shows Near You" : "Vendor Opportunities"}
            </h2>
          </div>
          <span className="text-xs sm:text-sm text-[#6C757D]">
            {loading ? "Searching..." : `${events.length} shows`}
          </span>
        </div>
      </div>

      {/* Event Grid */}
      <ScrollArea className="h-[calc(100vh-280px)] sm:h-[calc(100vh-300px)] min-h-[400px] sm:min-h-[500px]">
        <div className="p-3 sm:p-4" data-testid="event-list">
          {loading ? (
            <EventListSkeleton />
          ) : events.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {visibleEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onClick={onEventClick}
                    isSelected={selectedEvent?.id === event.id}
                    isSaved={savedShowIds?.has(event.id)}
                    onToggleSave={onToggleSave}
                  />
                ))}
              </div>
              {hasMore && (
                <button
                  onClick={() => setVisibleCount(prev => prev + EVENTS_PER_PAGE)}
                  className="w-full mt-4 py-3 rounded-lg border border-[#D6D3D1] bg-[#F7F5F0] hover:bg-[#E07A5F]/10 hover:border-[#E07A5F] text-sm font-medium text-[#2B2D42] flex items-center justify-center gap-2 transition-colors"
                  data-testid="load-more-btn"
                >
                  <ArrowDown weight="bold" className="w-4 h-4" />
                  Load More ({events.length - visibleCount} remaining)
                </button>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default EventList;
